import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { CompBadge, CompCard } from '../components/CompCard';
import { ProfileCard } from '../components/ProfileCard';
import { TeamGiftCard } from '../components/TeamGiftCard';
import { AppLink, useGo } from '../components/Nav';
import { VideosTable } from '../components/VideosTable';
import {
  ExternalLink,
  Fact,
  Facts,
  Identity,
  LoadMore,
  Mono,
  Rows,
  SearchBox,
  Section,
  SubscriptionBadge,
  Table,
  Td,
  Th,
  Tr,
} from '../components/data';
import { Badge, Button, ErrorState, Loading, PageHeader } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { stripeCustomerUrl, stripeSubscriptionUrl } from '../lib/env';
import { shortDate, timeAgo } from '../lib/format';
import { useDebounced } from '../lib/useDebounced';
import {
  fetchUser,
  fetchUsers,
  grantUserComp,
  qk,
  revokeUserComp,
  type Access,
  type MembershipRow,
} from '../queries/admin';

/** Premium, and where it comes from: the one thing support is asked about. */
function AccessBadge({ access }: { access: Access }) {
  if (!access.premium) return <Badge tone="neutral">Free</Badge>;
  return <Badge tone={access.source === 'comp' ? 'go' : 'info'}>{access.source === 'comp' ? 'Comped' : 'Premium'}</Badge>;
}

/** The same answer in a sentence, naming the team when a seat is what does it. */
function AccessState({ access, memberships }: { access: Access; memberships: MembershipRow[] }) {
  if (access.source === 'comp') return <>On premium because we gave it to them.</>;
  if (access.source === 'seat') {
    const via = memberships.filter((m) => m.seat === 'paid' && m.org.planLive);
    return <>On premium through a paid seat on {via.map((m) => m.org.name).join(', ')}.</>;
  }
  if (memberships.length === 0) return <>On the free plan, with no team behind them.</>;
  return <>On the free plan: no paid seat on a team whose plan is live.</>;
}

/** Stripe's customer and subscription for a team, when there are any. */
function StripeLinks({ org }: { org: MembershipRow['org'] }) {
  if (!org.stripeCustomerId && !org.stripeSubscriptionId) {
    return <span className="text-caption text-chalk-faint">no Stripe customer</span>;
  }
  return (
    <span className="flex flex-col gap-0.5 text-caption">
      {org.stripeCustomerId ? (
        <ExternalLink href={stripeCustomerUrl(org.stripeCustomerId)}>{org.stripeCustomerId}</ExternalLink>
      ) : null}
      {org.stripeSubscriptionId ? (
        <ExternalLink href={stripeSubscriptionUrl(org.stripeSubscriptionId)}>{org.stripeSubscriptionId}</ExternalLink>
      ) : null}
    </span>
  );
}

export function UsersPage() {
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim());
  const go = useGo();

  useEffect(() => {
    track('Viewed Admin Users');
  }, []);

  // Once the typing has settled, not once per keystroke.
  useEffect(() => {
    if (query) track('Admin Search Performed', { page: 'users' });
  }, [query]);

  const users = useInfiniteQuery({
    queryKey: qk.users(query),
    queryFn: ({ pageParam }) => fetchUsers(query, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const rows = users.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Everyone with a Slate account, newest first."
        actions={
          <AppLink to="/invites">
            <Button size="sm">Invite someone, comped</Button>
          </AppLink>
        }
      />
      <div className="mb-4">
        <SearchBox value={q} onChange={setQ} placeholder="Search by email or name" busy={users.isFetching && !users.isFetchingNextPage} />
      </div>

      <Table>
        <thead>
          <tr>
            <Th>Person</Th>
            <Th>Access</Th>
            <Th>Teams</Th>
            <Th align="right">Recordings</Th>
            <Th align="right">Joined</Th>
          </tr>
        </thead>
        <Rows query={users} columns={5} empty={query ? `Nobody matches "${query}".` : 'No users yet.'}>
          {rows.map((user) => (
            <Tr key={user.id} onClick={() => go('/users/$id', { params: { id: user.id } })}>
              <Td className="max-w-[20rem]">
                <Identity name={user.name} email={user.email} avatarUrl={user.avatarUrl} />
              </Td>
              <Td>
                <AccessBadge access={user.access} />
              </Td>
              <Td>
                <span className="flex flex-wrap gap-1">
                  {user.memberships.length === 0 ? (
                    <span className="text-chalk-faint">none</span>
                  ) : (
                    user.memberships.map((membership) => (
                      <Badge key={membership.org.id} tone={membership.seat === 'paid' ? 'info' : 'neutral'}>
                        {membership.org.name} · {membership.role}
                      </Badge>
                    ))
                  )}
                </span>
              </Td>
              <Td align="right">{user._count.videos.toLocaleString()}</Td>
              <Td align="right" className="whitespace-nowrap text-chalk-dim" title={user.createdAt}>
                {timeAgo(user.createdAt)}
              </Td>
            </Tr>
          ))}
        </Rows>
      </Table>
      <LoadMore query={users} />
    </>
  );
}

export function UserPage({ id }: { id: string }) {
  const user = useQuery({ queryKey: qk.user(id), queryFn: () => fetchUser(id) });

  if (user.isPending) {
    return (
      <>
        <PageHeader title="User" />
        <Loading />
      </>
    );
  }
  if (user.isError) {
    return (
      <>
        <PageHeader title="User" />
        <ErrorState message={errorMessage(user.error)} onRetry={() => void user.refetch()} />
      </>
    );
  }

  const data = user.data;

  return (
    <>
      <p className="mb-3 text-caption text-chalk-faint">
        <AppLink to="/users" className="hover:text-chalk-dim">
          Users
        </AppLink>{' '}
        /
      </p>
      <div className="flex items-start gap-4">
        {data.avatarUrl ? (
          <img src={data.avatarUrl} alt="" referrerPolicy="no-referrer" className="mt-1 h-12 w-12 shrink-0 rounded-full object-cover" />
        ) : null}
        <div className="min-w-0 flex-1">
          <PageHeader
            title={data.name?.trim() || data.email}
            subtitle={data.name ? data.email : undefined}
            actions={<AccessBadge access={data.access} />}
          />
        </div>
      </div>

      <Facts>
        <Fact label="Joined">
          {shortDate(data.createdAt)} <span className="text-chalk-faint">({timeAgo(data.createdAt)})</span>
        </Fact>
        <Fact label="Recordings">{data._count.videos.toLocaleString()}</Fact>
        <Fact label="Comments">{data._count.comments.toLocaleString()}</Fact>
        <Fact label="Request links">{data._count.requestLinks.toLocaleString()}</Fact>
        <Fact label="Desktop sessions">{data._count.deviceSessions.toLocaleString()}</Fact>
        <Fact label="Firebase uid">
          <Mono>{data.firebaseUid}</Mono>
        </Fact>
        <Fact label="User id">
          <Mono>{data.id}</Mono>
        </Fact>
      </Facts>

      <Section title="Profile">
        <ProfileCard user={data} />
      </Section>

      <Section title="Access">
        <div className="space-y-4">
          {data.memberships.length === 0 ? (
            <TeamGiftCard user={data} invalidates={[qk.user(data.id), ['admin', 'users'], ['admin', 'orgs']]} />
          ) : null}
          {data.memberships.length > 0 || data.access.comp ? (
            <CompCard
              title="Premium on the person"
              state={<AccessState access={data.access} memberships={data.memberships} />}
              target="user"
              blurb={
                data.memberships.length === 0
                  ? 'The older arrangement: the plan on the person, with no team behind it. The app has nowhere to show it, so prefer the team above; this stays so an existing grant can be edited or taken back.'
                  : "Comping is ours, not Stripe's: it needs no subscription, and nothing Stripe says later takes it away. One person, one seat, on top of whatever their team has. To put a whole team on the plan, and to say how many seats, comp the team instead."
              }
              comp={data.access.comp}
              grant={(body) => grantUserComp(data.id, body)}
              revoke={() => revokeUserComp(data.id)}
              invalidates={[qk.user(data.id), ['admin', 'users']]}
            />
          ) : null}
        </div>
      </Section>

      <Section title="Teams" count={data.memberships.length}>
        <Table>
          <thead>
            <tr>
              <Th>Team</Th>
              <Th>Role</Th>
              <Th>Seat</Th>
              <Th>Plan</Th>
              <Th>Stripe</Th>
            </tr>
          </thead>
          <tbody>
            {data.memberships.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-body text-chalk-faint">
                  Not on any team. Personal library only, and no Stripe customer of their own: billing belongs to
                  teams.
                </td>
              </tr>
            ) : (
              data.memberships.map((membership) => (
                <Tr key={membership.org.id}>
                  <Td>
                    <AppLink to="/teams/$id" params={{ id: membership.org.id }} className="text-chalk hover:underline">
                      {membership.org.name}
                    </AppLink>
                  </Td>
                  <Td className="capitalize">{membership.role}</Td>
                  <Td>
                    <Badge tone={membership.seat === 'paid' ? 'info' : 'neutral'}>{membership.seat}</Badge>
                  </Td>
                  <Td>
                    <span className="flex flex-wrap items-center gap-1.5">
                      <SubscriptionBadge status={membership.org.subscriptionStatus} />
                      <CompBadge comp={membership.org.comp} />
                    </span>
                  </Td>
                  <Td>
                    <StripeLinks org={membership.org} />
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </Section>

      <Section title="Recent recordings" count={data._count.videos}>
        <VideosTable videos={data.videos} showOwner={false} empty="Nothing recorded yet." />
      </Section>
    </>
  );
}
